"use client";

import { useEffect, useState, type FC, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { mergeClasses, padNumber } from "@huub-web/utils";
import {
  Container,
  type ContainerProps,
  Heading,
  type ClassNameProp,
  Text,
  mergeRefs,
  Flex,
  Card,
  Chip,
  ProgressBar,
  useIntersectionObserver,
} from "@huub-web/ui";

import type { GetGeoJSONHandler } from "../Analysis/Analysis";
import { Section } from "../Section";
import { useContentMenuSection } from "../../hooks/useContentMenuSection";
import { AnalysisIndicator } from "../AnalysisIndicator";
import {
  analysisSectionIllustrations,
  type AnalysisSectionIndex,
} from "../Analysis/types/AnalysisSections";
import { SummaryLoading } from "../Analysis/components/SummaryLoading";
import { Triptych } from "../Triptych";
import { Tip } from "../Tip";
import { InfoIcon } from "../InfoIcon";
import { MapboxMapCard } from "../MapboxMapCard";
import { getFoundationFeatureOpacity } from "../MapboxMapView";
import { progressBarStyle } from "./PropertyHazardProfileAnalysis.css";
import { AnalysisObservation } from "../AnalysisObservationsSection/components/AnalysisObservation";

/**
 * Sentinel value from the backend indicating the soil type could not be classified.
 */
const UNCLASSIFIABLE_SOIL_TYPE = "Niet indeelbaar";

/**
 * Backend riskCategory values for areas with less load-bearing soil
 * that have elevated foundation risk. Uses substring matching to handle
 * variations (e.g. "Getijdengebieden en afgesloten zeearmen").
 */
const HIGH_RISK_CATEGORIES = [
  "Laagveengebied",
  "Rivierengebied",
  "Zeekleigebied",
  "Getijdengebied",
];

export type PropertyHazardProfileAnalysisProps = ClassNameProp & {
  /**
   * Index of the section
   **/
  index?: AnalysisSectionIndex;
  /**
   * Container size
   **/
  container?: ContainerProps["size"];
  /**
   * Description of the foundational risks
   **/
  description?: string;
  /**
   * Recommendations for the foundation
   **/
  recommendations?: string[];
  /**
   * Risk level of the foundation
   **/
  riskLevel?: [number, number];
  /**
   * Category of the risk
   **/
  riskCategory?: string;
  /**
   * Predominant soil type in the postcode area (e.g. "Zeekleigebied")
   **/
  soilType?: string;
  /**
   * Percentage of the postcode area covered by the predominant soil type
   **/
  soilPercentage?: number;
  /**
   * Whether data is derived from a nearby postcode area
   **/
  derived?: boolean;
  /**
   * Whether the component is loading
   **/
  loading?: boolean;
  /**
   * Whether the component has no data
   **/
  noData?: boolean;
  /**
   * Mapbox access token
   **/
  mapBoxToken: string;
  /**
   * Center coordinates [latitude, longitude]
   **/
  center: [number, number];
  /**
   * Click handler
   **/
  onMapClick?: () => void;
  /**
   * Get GeoJSON handler
   **/
  getGeoJSON: GetGeoJSONHandler;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * PropertyHazardProfileAnalysis component displays foundational risks information
 * for a property
 */
export const PropertyHazardProfileAnalysis: FC<
  PropertyHazardProfileAnalysisProps
> = ({
  index,
  container = "marketing",
  description,
  recommendations,
  soilType,
  soilPercentage,
  derived,
  loading,
  noData,
  riskLevel,
  riskCategory,
  onMapClick,
  mapBoxToken,
  center,
  getGeoJSON,
  className,
  ref,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [geoJSON, setGeoJSON] = useState<GeoJSON.FeatureCollection | undefined>(
    undefined,
  );

  const { ref: intersectionRef, isIntersecting } =
    useIntersectionObserver<HTMLDivElement>(undefined, { triggerOnce: true });

  /**
   * Fetches foundation GeoJSON data when section becomes visible
   */
  useEffect(() => {
    if (!isIntersecting || loading) return;

    const fetchGeoJSON = async () => {
      setIsLoading(true);
      try {
        const data = await getGeoJSON("foundation");
        setGeoJSON(data);
      } catch (error) {
        console.error("Error fetching foundation GeoJSON:", error);
        setGeoJSON(undefined);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGeoJSON();
  }, [isIntersecting, getGeoJSON, loading]);

  const sectionRef = useContentMenuSection<HTMLDivElement>({
    id: "property-hazard-profile",
    label: t("propertyHazardProfileAnalysis.title"),
    index,
  });

  const id = `analysisSection${index}`;

  const showTooltip =
    riskLevel !== undefined &&
    soilType !== undefined &&
    soilType !== UNCLASSIFIABLE_SOIL_TYPE;

  const isHighRisk = HIGH_RISK_CATEGORIES.some((category) =>
    riskCategory?.includes(category),
  );

  /**
   * Soil type label shown as its own chip, including the coverage percentage
   * when available. Undefined when the soil type is missing or unclassifiable.
   */
  const soilLabel =
    soilType && soilType !== UNCLASSIFIABLE_SOIL_TYPE
      ? `${soilType}${
          soilPercentage !== undefined ? ` (${soilPercentage}%)` : ""
        }`
      : undefined;

  const tooltipText = showTooltip
    ? t(
        isHighRisk
          ? "propertyHazardProfileAnalysis.tooltipHighRisk"
          : "propertyHazardProfileAnalysis.tooltipLowRisk",
        {
          min: riskLevel?.[0],
          max: riskLevel?.[1],
          soilType,
        },
      )
    : undefined;

  return (
    <Section
      id={id}
      ref={mergeRefs(ref, sectionRef, intersectionRef)}
      className={mergeClasses(className)}
    >
      <Container size={container}>
        {index !== undefined && (
          <AnalysisIndicator
            index={index}
            illustration={analysisSectionIllustrations[index]}
          />
        )}
        <Heading
          title={t("propertyHazardProfileAnalysis.title")}
          description={
            loading || noData ? (
              <SummaryLoading />
            ) : (
              description || <SummaryLoading />
            )
          }
          variant="main"
        />

        {!noData && (
          <Triptych>
            <MapboxMapCard
              accessToken={mapBoxToken}
              center={center}
              controlsConfig={false}
              onClick={onMapClick}
              zoom={15.5}
              geoJSON={geoJSON}
              getGeoJSONFeatureOpacity={getFoundationFeatureOpacity}
              buttonValue={t("propertyHazardProfileAnalysis.openMap")}
            >
              {isLoading && (
                <ProgressBar
                  position="absolute"
                  align="top"
                  variant="infinite"
                  className={progressBarStyle}
                />
              )}
            </MapboxMapCard>

            {!loading && !noData && (
              <>
                {riskLevel && (
                  <Card border="default" overflowHidden>
                    <Flex align="center" gap="xs">
                      <Text bold as="h3">
                        {t("propertyHazardProfileAnalysis.riskLevel")}
                      </Text>
                      {tooltipText && <InfoIcon value={tooltipText} />}
                    </Flex>
                    <Text variant="displayL">
                      {riskLevel?.[0]}% &mdash; {riskLevel?.[1]}%
                    </Text>
                    <Text variant="bodyS" color="currentColor50">
                      {t("propertyHazardProfileAnalysis.riskLevelDescription")}
                    </Text>
                    {(riskCategory || soilLabel) && (
                      <Flex gap="xs" wrap>
                        {riskCategory && (
                          <Chip alignSelf="flex-start" variant="tertiary">
                            <Text
                              variant="surfaceSubtitle"
                              color="currentColor50"
                            >
                              {riskCategory}
                            </Text>
                          </Chip>
                        )}
                        {soilLabel && (
                          <Chip alignSelf="flex-start" variant="tertiary">
                            <Text
                              variant="surfaceSubtitle"
                              color="currentColor50"
                            >
                              {soilLabel}
                            </Text>
                          </Chip>
                        )}
                      </Flex>
                    )}
                  </Card>
                )}

                <Tip
                  name={t("propertyHazardProfileAnalysis.recommendations")}
                  trailing={
                    <Flex as="ul" direction="column" align="flex-start">
                      {recommendations?.map((recommendation) => (
                        <Flex
                          key={recommendation}
                          as="li"
                          gap="s"
                          align="center"
                        >
                          <Text>&bull;</Text>
                          <Text grow variant="bodyS">
                            {recommendation}
                          </Text>
                        </Flex>
                      ))}
                    </Flex>
                  }
                />
              </>
            )}

            {loading && (
              <AnalysisObservation
                loading
                title={t("propertyHazardProfileAnalysis.riskLevel")}
              />
            )}
          </Triptych>
        )}

        {derived && !loading && !noData && (
          <Text variant="bodyS" color="currentColor50">
            {t("propertyHazardProfileAnalysis.derivedDisclaimer")}
          </Text>
        )}
      </Container>

      {index !== undefined && (
        <Text align="center">
          {t("propertyHazardProfileAnalysis.indexTitle", {
            index: padNumber(index),
          })}
        </Text>
      )}
    </Section>
  );
};
